import cors from "cors";
import express, { CookieOptions } from "express";
import { Options as RateLimitOptions } from "express-rate-limit";
import cookieParser from "cookie-parser";
import compression from "compression";
import { Options as QueryParserOptions } from "../utils/helpers/query-parser.helpers";
import { ValidatorOptions } from "class-validator";
import { MsDuration } from "../modules/auth/utils/helpers/auth.controller.helpers";
import { OpenAPIV3 } from "openapi-types";
import type { ApiReferenceConfiguration } from "@scalar/express-api-reference" with { "resolution-mode": "import" };
import nodemailer from "nodemailer";
import { ModuleComponents } from "../utils/dynamic-loader";
import { ArkosRequestHandler } from ".";
import {
  AuthenticateHookHandler,
  AuthenticateAfterHookHandler,
  AuthenticateErrorHookHandler,
  AuthorizeHookHandler,
  AuthorizeAfterHookHandler,
  AuthorizeErrorHookHandler,
} from "./arkos-config/utils";

/**
 * Defines the initial configs of the api to be loaded at startup.
 */
export type ArkosConfig = {
  /**
   * Configuration for resolving your Arkos application's source files at runtime.
   *
   * @since 1.6.1-canary.2
   *
   */
  source?: {
    /**
     * The entry point file of your Arkos application, resolved at runtime.
     *
     * This should point to the file where `app.listen()` or `app.listen(server)`
     * is called to start the HTTP server.
     *
     * Supports both TypeScript and JavaScript projects.
     *
     * @type {string}
     *
     * @default "src/app.ts" (TypeScript) | "src/app.js" (JavaScript)
     *
     * @since 1.6.1-canary.2
     *
     * @example
     * ```ts
     * // TypeScript project
     * entryPoint: "src/app.ts"
     *
     * // JavaScript project
     * entryPoint: "src/app.js"
     *
     * // Custom path
     * entryPoint: "src/server/main.ts"
     * ```
     */
    entryPoint?: string;
  };
  /**
   * The global prefixer for the whole application
   *
   * @default "/api"
   */
  readonly globalPrefix?: string;
  /**
   * Allows to configure request configs
   */
  request?: {
    /**
     * Allows to configure request parameters
     */
    parameters?: {
      /**
       * Toggles allowing `VERY DANGEROUS` request paramateres under `req.query` for passing prisma query options.
       *
       * See more
       */
      allowDangerousPrismaQueryOptions?: boolean;
    };
  };
  /** Message you would like to send, as Json and 200 response when
   * ```curl
   * GET /api
   * ```
   *
   * ```json
   * { "message": "Welcome to YourAppName" }
   * ```
   *
   * default message is: Welcome to our Rest API generated by Arkos, find more about Arkos at www.arkosjs.com.
   *
   *
   *  */
  welcomeMessage?: string;
  /**
   * Port where the application will run, can be set in 3 ways:
   *
   * 1. default is 8000
   * 2. PORT under environment variables (Lower precedence)
   * 3. this config option (Higher precedence)
   */
  port?: number | undefined;
  /**
   * Allows to listen on a different host than localhost only
   */
  host?: string;
  /**
   * Defines authentication related configurations, by default is undefined.
   *
   * See [www.arkosjs.com/docs/core-concepts/authentication/setup](https://www.arkosjs.com/docs/core-concepts/authentication/setup) for details.
   */
  authentication?: {
    /**
     * Lifecycle hooks for the built-in authentication and authorization pipeline.
     *
     * These hooks only apply to **auto-generated Arkos routes**. On custom routes using
     * `authService.authenticate` directly, chain them manually as standard middlewares.
     *
     * Hooks run as standard Express middlewares — call `next()` to continue or
     * `next(error)` to abort and forward to the global error handler.
     *
     * @see {@link https://www.arkosjs.com/docs/core-concepts/authentication/hooks}
     */
    hooks?: {
      authenticate?: {
        /**
         * Runs before JWT extraction and verification.
         *
         * @example
         * ```ts
         * before: ({ req, skip, action, resource, rule }) => {
         *   req.authContext = { startedAt: Date.now() };
         * }
         * ```
         *
         * @example Skip core logic entirely (e.g. custom auth)
         * ```ts
         * before: ({ req, skip, action, resource, rule }) => {
         *   req.user = myCustomAuth(req);
         *   skip();
         * }
         * ```
         */
        before?: AuthenticateHookHandler | AuthenticateHookHandler[];

        /**
         * Runs after `req.user` has been set.
         *
         * @example
         * ```ts
         * after: ({ req }) => {
         *   if (!req.user.hasChangedPassword) {
         *     throw new AppError("Password change required.", 403, "PasswordChangeRequired");
         *   }
         * }
         * ```
         */
        after?: AuthenticateAfterHookHandler | AuthenticateAfterHookHandler[];

        /**
         * Runs when authentication throws — invalid token, expired token, user not found, etc.
         * Throw to forward to the global error handler, or call `skip()` to suppress and jump to `after` hooks.
         *
         * @example
         * ```ts
         * onError: ({ req, error, skip, action, resource, rule }) => {
         *   console.warn(`Auth failed:`, error);
         *   throw error;
         * }
         * ```
         *
         * @example Suppress error and continue
         * ```ts
         * onError: ({ req, skip, action, resource, rule }) => {
         *   req.user = guestUser;
         *   skip();
         * }
         * ```
         */
        onError?: AuthenticateErrorHookHandler | AuthenticateErrorHookHandler[];
      };

      authorize?: {
        /**
         * Runs before the role/permission check.
         *
         * @example
         * ```ts
         * before: ({ req }) => {
         *   if (req.headers["x-elevated-access"] === process.env.ELEVATION_KEY) {
         *     req.user.role = "admin";
         *   }
         * }
         * ```
         *
         * @example Skip permission check entirely
         * ```ts
         * before: ({ req, skip, action, resource, rule }) => {
         *   req.user.role = myCustomRoleResolver(req);
         *   skip();
         * }
         * ```
         */
        before?: AuthorizeHookHandler | AuthorizeHookHandler[];

        /**
         * Runs after the permission check passes.
         *
         * @example
         * ```ts
         * after: ({ req }) => {
         *   auditLog.record({ userId: req.user.id, path: req.path });
         * }
         * ```
         */
        after?: AuthorizeAfterHookHandler | AuthorizeAfterHookHandler[];

        /**
         * Runs when the user lacks sufficient permissions (403).
         * Throw to forward to the global error handler, or call `skip()` to suppress and jump to `after` hooks.
         *
         * @example
         * ```ts
         * onError: ({ req, error, action, resource, rule }) => {
         *   auditLog.record({ userId: req.user?.id, reason: "insufficient_permissions" });
         *   throw error;
         * }
         * ```
         *
         * @example Suppress error and continue
         * ```ts
         * onError: ({ req, skip, action, resource, rule }) => {
         *   req.user.role = "guest";
         *   skip();
         * }
         * ```
         */
        onError?: AuthorizeErrorHookHandler | AuthorizeErrorHookHandler[];
      };
    };
    enabled?: boolean;
    /**
     * Defines whether to use Static or Dynamic Role-Based Acess Control
     *
     * Visit [www.arkosjs.com/docs/core-concepts/authentication/setup](https://www.arkosjs.com/docs/core-concepts/authentication/setup) for more details.
     */
    mode: "static" | "dynamic";
    /**
     * Defines auth login related configurations to customize the api.
     */
    login?: {
      /**
       * Defines the field that will be used as username by the built-in auth system, by default arkos will look for the field "username" in your model User, hence when making login for example you must send:
       *
       * ```json
       *  {
       *    "username": "johndoe",
       *    "password": "somePassword123"
       *  }
       * ```
       *
       * **Note:** You can also modify the usernameField on the fly by passing it to the request query parameters. example:
       *
       * ```curl
       * POST /api/auth/login?usernameField=email
       * ```
       *
       * See more at [www.arkosjs.com/docs/core-concepts/authentication/setup#login-with-different-fileds](https://www.arkosjs.com/docs/core-concepts/authentication/setup#login-with-different-fileds)
       *
       * By specifing here another field for username, for example passing "email", "companyCode" or something else your json will be like:
       *
       * **Example with email**
       *
       * ```json
       *  {
       *    "email": "john.doe@example.com",
       *    "password": "somePassword123"
       *  }
       * ```
       */
      allowedUsernames?: string[];
      /** Defines wether to send the access token in response after login or only send as cookie, defeault is both.*/
      sendAccessTokenThrough?: "cookie-only" | "response-only" | "both";
    };
    /**
     * Specifies the regex pattern used by the authentication system to enforce password strength requirements.
     *
     * **Important**: If using validation libraries like Zod or class-validator, this will be completely overwritten.
     *
     * **Default**: ```/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/``` - Ensures the password contains at least one uppercase letter, one lowercase letter, and one numeric digit.
     *
     * **message**: (Optional) A custom error message to display when the password does not meet the required strength criteria.
     * @deprecated will stop working on v2.0
     */
    passwordValidation?: { regex: RegExp; message?: string; };
    /**
     * Allows to specify the request rate limit for all authentication endpoints but `/api/users/me`.
     * 
     * #### Default
     *{
        windowMs: 5000,
        limit: 10,
        standardHeaders: "draft-7",
        legacyHeaders: false,
      }
     * 
     * Passing an object not overriding all the default options will only
     * cause it to be deepmerged and not actually replace with empty fields
     * 
     *@see This is are the options used on the `express-rate-limit` npm package used on epxress. read more about [https://www.npmjs.com/package/express-rate-limit](https://www.npmjs.com/package/express-rate-limit)
     */
    rateLimit?: Partial<RateLimitOptions>;
    /**
     * JWT (JSON Web Token) authentication configuration.
     *
     * You can override these values directly in code, or use environment variables:
     *
     * - `JWT_SECRET`: Secret used to sign and verify JWT tokens.
     * - `JWT_EXPIRES_IN`: Duration string or number indicating when the token should expire (e.g. "30d", 3600).
     * - `JWT_COOKIE_SECURE`: Whether the cookie is sent only over HTTPS. Default: `true` in production.
     * - `JWT_COOKIE_HTTP_ONLY`: Whether the cookie is HTTP-only. Default: `true`.
     * - `JWT_COOKIE_SAME_SITE`: Can be "lax", "strict", or "none". Defaults to "lax" in dev, "none" in prod.
     *
     *@see Values passed here take precedence over environment variables.
     */
    jwt?: {
      /** Secret key used for signing and verifying JWT tokens */
      secret?: string;
      /**
       * Duration after which the JWT token expires.
       * Accepts either a duration string (e.g. "30d", "1h") or a number in milliseconds.
       * Defaults to "30d" if not provided.
       */
      expiresIn?: MsDuration | number;
      /**
       * Configuration for the JWT cookie sent to the client
       */
      cookie?: {
        /**
         * Whether the cookie should be marked as secure (sent only over HTTPS).
         * Defaults to `true` in production and `false` in development.
         *
         * @env `JWT_COOKIE_SECURE`
         */
        secure?: boolean;
        /**
         * Whether the cookie should be marked as HTTP-only.
         * Default is `true` to prevent access via JavaScript.
         *
         * @env `JWT_COOKIE_HTTP_ONLY`
         */
        httpOnly?: boolean;
        /**
         * Controls the SameSite attribute of the cookie.
         * Defaults to "none" in production and "lax" in development.
         * Options: "lax" | "strict" | "none"
         *
         * @env `JWT_COOKIE_SAME_SITE`
         */
        sameSite?: "lax" | "strict" | "none";
        /**
         * Expiry date of the cookie in GMT. If not specified (undefined), creates a session cookie.
         *
         * @default `now() + authentication.jwt.expireIn | JWT_EXPIRES_IN`
         *
         * @since 1.5.11-beta
         */
        expires?: Date | undefined;
        /**
         * Domain for the cookie. Use a leading dot (e.g. `.example.com`) to include all subdomains.
         *
         * @env `JWT_COOKIE_DOMAIN`
         *
         * @since 1.5.11-beta
         */
        domain?: string | undefined;
      } & Omit<
        CookieOptions,
        "secure" | "httpOnly" | "sameSite" | "expires" | "domain"
      >;
    };
  };
  /** Allows to customize and toggle the built-in validation, by default it is set to `false`. If true is passed it will use validation with the default resolver set to `class-validator` if you intend to change the resolver to `zod` do the following:
   *
   *```ts
   * const config = defineConfig({
   *    validation: {
   *        resolver: "zod"
   *    }
   * })
   * ```
   *
   * @See [www.arkosjs.com/docs/guides/validation/setup](https://www.arkosjs.com/docs/guides/validation/setup) for more details.
   */
  validation?: {
    /**
     * Defines whether to use strict request validation, it means in every request using `ArkosRouter()` you must pass the validation options with all options (e.g: query, params, body, etc.).
     *
     * **How It Works:**
     * - If it is true, Arkos will require all the options a stated above on the routes and if you don't pass it will throw an error.
     * **What if you don't want to pass a validator to some options?**
     * - You will need to pass `undefined` to the validator option (see example below), this way Arkos will throw an error if something is passed to `req.query` if you passed undefined as validator into `validation.query`
     *
     * ```ts
     * import { ArkosRouter } from "arkos"
     *
     * const router = ArkosRouter()
     *
     * router.get({
     *  route: "/api/posts",
     *  validation: {
     *    query: undefined
     *  }
     * })
     *
     * ```
     *
     * @since 1.4.0-beta
     */
    strict?: boolean;
  } & (
    | {
      resolver: "class-validator";
      /**
       * ValidatorOptions to used while validating request data.
       *
       * **Default**:
       * ```ts
       * {
       *  whitelist: true
       *  forbidNonWhitelisted: true
       * }
       * ```
       */
      validationOptions?: ValidatorOptions;
    }
    | {
      resolver: "zod";
      /**
       * @since v1.5.0-beta
       */
      validationOptions?: {
        /**
         * Throws an error for know whitelisted fields
         *
         * @default true
         */
        forbidNonWhitelisted?: boolean;
      };
    }
  );
  /**
   * Defines file upload configurations
   *
   * See {@link https://www.arkosjs.com/docs/guides/file-handling/file-uploads/setup}
   */
  fileUpload?: {
    /**
     * Disables built-in file upload
     *
     * @since 1.5.11-beta
     */
    enabled?: boolean;
    /**
     * Defiens the base file upload directory, default is set to /uploads (on root directory)
     *
     * When setting up a path dir always now that root directory will be the starting reference.
     *
     * #### Example
     * passing `../my-arkos-uploaded-files`
     *
     * Will save uploaded files one level outside the root dir inside `my-arkos-uploaded-files`
     *
     * NB: You must be aware of permissions on your server to acess files outside your project directory.
     *
     */
    baseUploadDir?: string;
    /**
     * Changes the default `/api/uploads` base route for accessing file upload route.
     *
     * #### IMPORTANT
     * Changing this will not affect the `baseUploadDir` folder. You can
     * pass here `/api/files/my-user-files` and `baseUploadDir` be `/uploaded-files`.
     *
     */
    baseRoute?: string;
    /**
     * Defines options for `express.static(somePath, someOptions)`
     *
     * #### Default:
     *
     * ```ts
     *{
          maxAge: "1y",
          etag: true,
          lastModified: true,
          dotfiles: "ignore",
          fallthrough: false,
          index: false,
          cacheControl: true,
        }
     * ```
     * 
     * By passing your custom options have in mind that it
     * will be deepmerged with the default.
     * 
     * Visit [https://expressjs.com/en/4x/api.html#express.static](https://expressjs.com/en/4x/api.html#express.static) for more understanding.
     * 
     */
    expressStatic?: Parameters<typeof express.static>[1];
    /**
     * Defines upload restrictions for each file type: image, video, document or other.
     *
     * #### Important:
     * Passing an object without overriding everything will only cause it
     * to be deepmerged with the default options.
     *
     * See [www.arkosjs.com/docs/reference/default-supported-upload-files](https://www.arkosjs.com/docs/reference/default-supported-upload-files) for detailed explanation about default values.
     * ```
     */
    restrictions?: {
      images?: {
        maxCount?: number;
        maxSize?: number;
        supportedFilesRegex?: RegExp;
      };
      videos?: {
        maxCount?: number;
        maxSize?: number;
        supportedFilesRegex?: RegExp;
      };
      documents?: {
        maxCount?: number;
        maxSize?: number;
        supportedFilesRegex?: RegExp;
      };
      files?: {
        maxCount?: number;
        maxSize?: number;
        supportedFilesRegex?: RegExp;
      };
    };
  };
  /**
   * Defines express middlewares configurations
   */
  middlewares?: {
    /**
     * Allows to define options for npm package compression
     * Nothing is passed by default.
     *
     * See [www.npmjs.com/package/compression](https://www.npmjs.com/package/compression) for further details.
     */
    compression?: false | compression.CompressionOptions | ArkosRequestHandler;
    /**
   * Allows to specify the request rate limit for all endpoints.
   * 
   * #### Default
   *```ts
   *{
      windowMs: 60 * 1000,
      limit: 1000,
      standardHeaders: "draft-7",
      legacyHeaders: false,
    }
    ```
   * 
   * Passing an object not overriding all the default options will only
   * cause it to be deepmerged and not actually replace with empty fields
   * 
   * This is are the options used on the `express-rate-limit` npm package used on epxress. read more about [https://www.npmjs.com/package/express-rate-limit](https://www.npmjs.com/package/express-rate-limit)
   */
    rateLimit?: false | Partial<RateLimitOptions> | ArkosRequestHandler;
    /**
     * Configuration for CORS (Cross-Origin Resource Sharing).
     *
     * Accepts:
     * - `false` — disables CORS middleware entirely.
     * - `cors.CorsOptions` — passed directly to the `cors` middleware.
     * - `cors.CorsOptionsDelegate` — a delegate function passed directly to the `cors` middleware.
     * - `ArkosRequestHandler` — a full Express middleware that replaces the cors middleware entirely.
     * - An object with deprecated `allowedOrigins`, `options`, and `customHandler` properties.
     *
     * In development, defaults to `origin: true, credentials: true`.
     * In production, defaults to `origin: "*"` (equivalent to plain `cors()`).
     *
     * See https://www.npmjs.com/package/cors
     */
    cors?:
    | false
    | cors.CorsOptions
    | cors.CorsOptionsDelegate
    | {
      /**
       * Defines allowed origins to access the API.
       *
       * @deprecated Use `cors: { origin: string | string[] }` (cors.CorsOptions) directly instead.
       */
      allowedOrigins?: string | string[] | "*" | true;
      /**
       * Additional cors options.
       *
       * @deprecated Pass cors.CorsOptions directly instead: `cors: { origin: "...", credentials: true }`.
       */
      options?: cors.CorsOptions;
      /**
       * If you would like to override the entire middleware.
       *
       * @deprecated Pass the handler directly instead: `cors: myCorsHandler`.
       */
      customHandler?: cors.CorsOptionsDelegate;
    }
    | ArkosRequestHandler;
    /**
     * Defines options for the built-in express.json() middleware
     * Nothing is passed by default.
     */
    expressJson?:
    | false
    | Parameters<typeof express.json>[0]
    | ArkosRequestHandler;
    /**
     * Allows to pass paremeters to cookieParser from npm package cookie-parser
     * Nothing is passed by default.
     *
     * See [www.npmjs.com/package/cookie-parser](https://www.npmjs.com/package/cookie-parser) for further details.
     */
    cookieParser?:
    | false
    | {
      secret?: string | string[];
      options?: Parameters<typeof cookieParser>[1];
    }
    | ArkosRequestHandler;
    /**
   * Options to define how query must be parsed.
   *
   * #### for example:
   * ```
   * GET /api/product?saleId=null
   * ```
   *
   * Normally would parsed to { saleId: "null" } so query parser
   * trough setting option `parseNull` will transform { saleId: null }
   * 
   * #### Default:
   * 
   * {
      parseNull: true,
      parseUndefined: true,
      parseBoolean: true,
      parseNumber: true,
    }
   * 
   * parseNumber may convert fields that are string but you only passed
   * numbers to query pay attention to this.
   * 
   * Soon a feature to converted the query to the end prisma type will be added.
   */
    queryParser?: false | QueryParserOptions | ArkosRequestHandler;
    /**
     * Configuration for request logger middleware.
     *
     * Logs incoming HTTP requests with details such as method, URL, status code, and response time.
     * Useful for debugging and monitoring API traffic.
     */
    requestLogger?: false | ArkosRequestHandler;
    /**
     * Configuration for global error handler middleware.
     *
     * Catches and processes errors thrown in route handlers and other middlewares.
     * Provides a centralized way to format error responses and log errors.
     */
    errorHandler?: false | express.ErrorRequestHandler;
  };
  /**
   * Defines express/arkos routers configurations
   */
  routers?: {
    /**
     * Strict mode configuration for routing security
     *
     * @default false
     * - all CRUD + Auth (if using auth) endpoits are enabled and avialable by default.
     *
     * When enabled:
     * - If `true`: All CRUD endpoints start disabled and must be explicitly enabled
     *   in each model's router configuration
     * - If `'no-bulk'`: Bulk operations (createMany, updateMany, deleteMany) are disabled
     *   by default, while individual operations can be enabled per model
     *
     * This provides enhanced security by ensuring no routes are accidentally exposed.
     * Use this in production environments to follow the principle of least privilege.
     *
     * See documentation: https://www.arkosjs.com/docs/guide/security#strict-mode
     */
    strict?: boolean | "no-bulk";
    /**
     * Replace the default welcome endpoint handler
     * @param req Express request object
     * @param res Express response object
     * @param next Express next function
     */
    welcomeRoute?: false | ArkosRequestHandler;
  };
  /**
   * Allows to configure email configurations for sending emails through `emailService`
   *
   * See [www.arkosjs.com/docs/guides/email-service](https://www.arkosjs.com/docs/guides/email-service)
   */
  email?: {
    /**
     * Email name to used like:
     *
     * John Doe\<john.doe@gmail.com>
     */
    name?: string;
    /**
     * Your email provider url
     */
    host: string;
    /**
     * Email provider SMTP port, Default is `465`
     */
    port?: number;
    /**
     * If smtp connection must be secure, Default is `true`
     */
    secure?: boolean;
    /**
     * Used to authenticate in your smtp server
     */
    auth: {
      /**
       * Email used for auth as well as sending emails
       */
      user: string;
      /**
       * Your SMTP password
       */
      pass: string;
    };
  } & Parameters<typeof nodemailer.createTransport>[0];
  /**
   * Defines Swagger and OpenApi specifications for auto generating swagger documentation UI using whether it is Prisma Schemas, Class-validator DTOs, Zod Schemas or JSON-Schemas.
   *
   * **Important**: Is worth mentioning that this works alongside `@scalar/express-api-reference` npm package, you define it's ApiReferenceConfiguration under ``. also consider checking the package documentation at []
   *
   * **Usage**
   *
   * ```ts
   * const config = defineConfig({
   *    // other configs
   *    swagger: {
   *      mode: "zod",
   *      options: {
   *        defintion: {
   *          info: {
   *            openapi: "3.0.0",
   *            title: "API Generated By Arkos.js",
   *            description: "This API was automatically generated by Arkos.js read more about at www.arkosjs.com"
   *          }
   *       }
   *    }
   *  }
   * })
   * ```
   * @see {@link https://www.arkosjs.com/docs/guides/open-api-documentation/setup}
   */
  swagger?: {
    /**
     * By default Arkos will disable API Documentation when the project is built `npm run build`, it does not matter what node environment is set. If you want to use it even after the `arkos build` command just set this to `true`.
     * */
    enableAfterBuild?: boolean;
    /**
     * Whether to require superUser authentication to access docs in production.
     * Default: true after build
     */
    authenticate?: boolean;
    /**
     * Endpoint where the Swagger UI will be available.
     *
     * @default "/api/api-docs"
     */
    endpoint?: string;
    /**
     * Determines how your API schemas should be generated.
     *
     * - "prisma": Generates schemas based on Prisma models
     * - "class-validator": Uses class-validator and class-transformer DTO classes
     * - "zod": Uses OpenAPI-compliant schemas directly
     *
     */
    mode: "prisma" | "class-validator" | "zod";
    /**
     * Allows `Arkos.js` to fallback to prisma schema and use them as json schema for defining request body and response data
     * when a given zod Schema or class-validator Class is not found to be transformed to json schema.
     *
     * @default false
     */
    strict?: boolean;
    /**
     * Defines your swagger configurations
     */
    options?: {
      /**
       * Swagger definition according to OpenAPI Specification.
       */
      definition?: {
        /**
         * OpenAPI version.
         *
         * @default "3.0.0"
         */
        openapi?: string;

        /**
         * Information about your API.
         */
        info?: {
          /**
           * Title of the API documentation.
           *
           *
           * @example "My API"
           *
           * @default "API Generated By Arkos.js"
           */
          title?: string;

          /**
           * Version of the API.
           *
           * @example "1.0.0"
           */
          version?: string;

          /**
           * Description of the API.
           *
           * @default "This API was automatically generated by Arkos.js read more about at www.arkosjs.com"
           */
          description?: string;
        };

        /**
         * Server configurations for the API.
         *
         * @default [{ url: "http://localhost:8000", description: "Development server" }]
         *
         * This can be overridden automatically by Arkos based on CLI, .env, or config.
         */
        servers?: {
          /**
           * Base URL of the server.
           *
           * @example "http://localhost:8000"
           */
          url: string;

          /**
           * Human-readable description of the server.
           *
           * @example "Development Server"
           */
          description?: string;
        }[];
        /**
         * Allows adding more paths for your OpenAPI documentation
         *
         * */
        paths?: OpenAPIV3.PathsObject;
        /**
         * (Optional) Additional metadata such as terms of service, contact, or license.
         *
         * See: https://swagger.io/specification/#infoObject
         */
        termsOfService?: string;
        contact?: {
          name?: string;
          url?: string;
          email?: string;
        };
        license?: {
          name: string;
          url?: string;
        };

        /**
         * Tags to group and describe endpoints.
         */
        tags?: {
          name: string;
          description?: string;
        }[];

        /**
         * Security definitions (e.g., Bearer Auth).
         */
        components?: {
          securitySchemes?: Record<string, any>;
          schemas?: Record<string, any>;
        };

        /**
         * Global security requirements.
         */
        security?: Array<Record<string, string[]>>;
      };

      /**
       * Glob patterns defining where Swagger should look for API route definitions and comments.
       *
       * @default 
       * ```ts
       *  ["./src/routers/*.router.{ts,js}", "./src/modules/**\/*.router.{ts,js}"]
        ```
       *
       * Adjust depending on TypeScript or JavaScript usage.
       */
      apis?: string[];

      /**
       * Enables deep linking in the Swagger UI.
       *
       * @default true
       */
      deepLinking?: boolean;

      /**
       * Enable/disable the "Try it out" button globally.
       *
       * @default true
       */
      tryItOutEnabled?: boolean;

      /**
       * Whether to persist authorization data across page reloads.
       *
       * @default false
       */
      persistAuthorization?: boolean;
    };
    /**
     * Custom `@scalar/express-api-reference` package options.
     *
     * @see {@link https://guides.scalar.com/scalar/scalar-api-references/integrations/express}
     */
    scalarApiReferenceConfiguration?: Partial<ApiReferenceConfiguration>;
  };
  /**
   * Helps in debugging some of the variables values that are used inside arkos from dynamic loaded components towhat is used into the generated api.
   *
   */
  debugging?: {
    /**
     * Controls debugging over HTTP request level
     */
    requests?: {
      level?: 0 | 1 | 2 | 3;
      filter?: (
        | "Query"
        | "Body"
        | "Params"
        | "TransformedQuery"
        | "ServiceArgs"
        | "PrismaFinalQueryArgs"
      )[];
    };
    /**
     * Controls debugging over the dynamic loaded modules and it's components on app starting
     */
    dynamicLoader?: {
      /**
       * Adjusts logging details
       *
       * 1 - Loaded modules and list of it's components files
       * 2 - All from 1 and detailed inspection per module component
       * 3 - All from 1 and 2, plus the final router component after merged with autho generated router.
       */
      level?: 0 | 1 | 2 | 3;
      filters?: {
        /**
         * Allows filtering by specific modules.
         *
         * Supports only searching for the starting parts of the module name, e.g: If there is`user-profile`, `user`, `posts`, passing `["user"]` will match both `user-profile` and `user`.
         */
        modules?: string[];
        /**
         * Allows filtering by component's names
         */
        components?: keyof ModuleComponents[];
      };
    };
  };
  /**
   * Allows to suppress specific warnings emitted by Arkos at startup or runtime.
   *
   * @since 1.5.10-beta
   */
  warnings?: {
    /**
     * Suppresses specific warning messages.
     */
    suppress?: {
      /**
       * Suppresses Prisma-related warnings.
       */
      prisma?: {
        /**
         * Suppresses the warning emitted when no Prisma instance is found.
         */
        noInstanceFound?: boolean;
        /**
         * Suppresses the warning emitted when no Prisma schema is found.
         */
        noSchemaFound?: boolean;
      };
    };
  };
  /**
   * Defines static file serving configurations.
   *
   * Arkos will automatically serve files from the configured folder using `express.static`.
   * If the folder does not exist at startup, a warning will be emitted.
   *
   * **Note:** The static files route is **not** prefixed with the `globalPrefix`.
   *
   * @since 1.7.0-canary.16
   *
   * @example
   * ```ts
   * import { defineConfig } from "arkos"
   *
   * export default defineConfig({
   *   staticFiles: {
   *     folder: "assets",
   *     prefix: "/static",
   *   }
   * })   */
  staticFiles?: {
    /**
     * Whether to enable static file serving.
     *
     * Set to `false` to disable static file serving entirely and suppress
     * the warning when the folder is not found.
     *
     * @default true
     */
    enabled?: boolean;

    /**
     * The folder to serve static files from, relative to the project root.
     *
     * @default "public"
     */
    folder?: string;

    /**
     * The URL path prefix under which static files will be served.
     *
     * **Note:** This is not affected by `globalPrefix`.
     *
     * @default "/"
     *
     * @example "/static" // files served at /static/image.png
     */
    prefix?: string;

    /**
     * Options passed directly to `express.static()`.
     *
     * By passing custom options, they will be deep merged with the defaults.
     *
     * #### Default:
     * ```ts
     * {
     *   maxAge: "1y",
     *   etag: true,
     *   lastModified: true,
     *   dotfiles: "ignore",
     *   fallthrough: true,
     *   index: false,
     *   cacheControl: true,
     * }
     * ```
     *
     * @see {@link https://expressjs.com/en/4x/api.html#express.static}
     */
    expressStatic?: Parameters<typeof express.static>[1];
  };
};
